import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SabisButton2Component, SabisComboBox2Component, SabisNgxCheckboxComponent, SabisNgxDialogComponent, SabisTextArea2Component, } from 'sabis-components2';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RecruitmentService, SaveApplicationStatusCDTO, UpdateApplicationStatusDTO } from '../recruitment/recruitment.service';
import { finalize } from 'rxjs';
import { AppNotificationsService } from '../../core/notifications/app-notifications.service';
import { EmailEditorDTO, EmptyEmailEditorDTO } from '../../shared/email/models/api-models';
import { ApplicationStatusLookupItemDTO, ApplicationStatusActiveReasonDTO } from '../../shared/api-common-models';
import { RecruitmentStepsService, StepAction } from '../../settings/recruitment-steps/recruitment-steps.service';
import { AppLoaderService } from '../../@theme/shared/services/app-loader/app-loader.service';
import { EmailComponent } from '../../shared/email/email.component';
import { SaveCommentCommand } from '../recruitment/comments/comments.service';

@Component({
  selector: 'sca-job-application-status',
  templateUrl: './job-application-status.component.html',
  styleUrls: ['./job-application-status.component.scss'],
  imports: [
    FormsModule,
    SabisNgxDialogComponent,
    SabisButton2Component,
    SabisComboBox2Component,
    EmailComponent,
    SabisNgxCheckboxComponent,
    SabisTextArea2Component
  ],
})
export class JobApplicationStatusComponent implements OnInit {
  public isLoading: boolean = true;
  public isSaving: boolean = false;
  public isSendEmailReadonly: boolean = false;
  public isCommentsRequired: boolean = false;

  //Job Application Statuses
  public selectedJobApplicationStatusId: number | null = 0;
  public jobApplicationStatuses: ApplicationStatusLookupItemDTO<any>[] = [];
  public selectedApplicationStatusReasonId: number | null = null;
  public applicationStatusReasons: ApplicationStatusActiveReasonDTO[] = [];
  public selectedApplicationStatusReasons: ApplicationStatusActiveReasonDTO[] = [];

  public sendEmail: boolean = false;
  public email: EmailEditorDTO = EmptyEmailEditorDTO;
  @ViewChild('emailForm') emailForm!: EmailComponent;

  public noSelection: boolean = false;

  // allowed email template ids set in settings > selection steps > email templates
  public allowedEmailTemplateIds: number[] = [];

  public showSendEmail: boolean = false;
  public emailTemplatesIds: number[] = [];

  public comments: string = '';
  public saveCommentCommand: SaveCommentCommand | undefined;

  constructor(public dialogRef: MatDialogRef<JobApplicationStatusComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApplicationStatusDialogData,
    private _recruitmentService: RecruitmentService,
    private _appNotification: AppNotificationsService,
    private _recruitmentStepsService: RecruitmentStepsService,
    private _appLoader: AppLoaderService) { }

  ngOnInit(): void {
    this.isLoading = true;
    this._appLoader.showLoading;

    this._recruitmentService.getApplicationStatusesInitData(this.data.applicationId, this.data.isRejection).subscribe(r => {
      this.noSelection = this.data.isRejection;
      if (this.noSelection) {
        this.selectedJobApplicationStatusId = r.applicationStatusLookupItems[0].itemId;
        this.selectedApplicationStatusReasonId = r.applicationStatusReasons[0].applicationStatusReasonId;
      }
      this.jobApplicationStatuses = r.applicationStatusLookupItems;
      this.applicationStatusReasons = r.applicationStatusReasons;
      this.isLoading = false;
      this.email.toEmail = this.data.candidateEmail;
    });

    /**
     if selectionStepId is not null that means that application status is being changed from action on step (cancel, reject,...) --> get allowed email templates from step
     otherwise when selectionStepId is null then the application status is being changed manually --> all email templates are allowed
    */
    if (this.data.selectionStepId) {
      // get allowed email template ids set up in Settings > Selection Steps > Email Templates
      this._recruitmentStepsService.getAllowedEmailTemplatesIds(this.data.applicationId, this.data.selectionStepId, StepAction.Reject).subscribe(allowedEmailTemplateIds => {
        this.emailTemplatesIds = allowedEmailTemplateIds;
        // on postpone meeting, send email is selected when postpone meeting dialog is opened
        this.sendEmail = this.data.isSendEmailSelected;
        this._appLoader.hideLoading;
      });
    }
  }

  public onApplicationStatusReasonChange(): void {
    let reason = this.applicationStatusReasons.find(reason => reason.applicationStatusReasonId === this.selectedApplicationStatusReasonId);
    this.isCommentsRequired = reason!.isCommentsRequired;
  }

  public onCloseButtonClick(): void {
    this.dialogRef.close();
  }

  public onSaveButtonClick(): void {
    if (this.isSaving) {
      return;
    }

    if (!this.selectedJobApplicationStatusId || (this.emailForm && !this.emailForm.validate())) {
      return;
    }

    const statusRequiresReason = this.applicationStatusReasons.filter(r => r.applicationStatuses.includes(this.selectedJobApplicationStatusId!)).length > 0;

    if (statusRequiresReason &&
      ((!this.selectedApplicationStatusReasonId)
        ||
        (this.isCommentsRequired && !this.comments))) {
      return;
    }

    let saveCommentsCommand: SaveCommentCommand = {
      comment: this.comments,
      recruitmentStepId: this.data.selectionStepId,
      attachedFilesIds: []
    }

    // prepare command for sending email
    let command: SaveApplicationStatusCDTO = {
      email: null,
      jobApplicationStatusId: this.selectedJobApplicationStatusId,
      selectionStepId: this.data.selectionStepId,
      saveCommentCommand: saveCommentsCommand,
      applicationStatusReasonId: this.selectedApplicationStatusReasonId
    };
    if (this.sendEmail) {
      command.email = {
        body: this.email.body,
        emailTemplateId: this.email.emailTemplateId!,
        fromEmail: this.email.fromEmail,
        priorityId: this.email.priorityId!,
        subject: this.email.subject,
        toEmail: this.email.toEmail,
        ccEmail: this.email.ccEmail,
        scheduledDate: this.email.isScheduledForLater == false ? undefined : this.email.scheduledDate,
        isScheduledForLater: this.email.isScheduledForLater,
        attachments: this.email.attachments
      }
    }

    this.isSaving = true;

    let request = this._recruitmentService.setApplicationStatus(this.data.applicationId, command);

    request.pipe(
      finalize(() => {
        this.isSaving = false;
      })
    ).subscribe({
      next: (r: UpdateApplicationStatusDTO) => {
        this._appNotification.notifySaveSucceeded();
        this.dialogRef.close(true);
      },
      error: () => {
        this._appNotification.notifySaveFailed();
      }
    });
  }

  // To hide/show send email checkbox and get the required email templates
  public onApplicationStatusChanged(): void {
    let selectedJobApplicationStatus = this.jobApplicationStatuses.find((j) => j.itemId == this.selectedJobApplicationStatusId);

    if (!selectedJobApplicationStatus) {
      return;
    }

    this.selectedApplicationStatusReasons = this.applicationStatusReasons.filter(r => r.applicationStatuses.includes(this.selectedJobApplicationStatusId!));

    if (!selectedJobApplicationStatus.canSendEmail) {
      this.showSendEmail = false;
      return;
    }
    this.showSendEmail = true;

    // get email template ids based on type
    this._recruitmentStepsService.getEmailTemplatesByTypeIds(selectedJobApplicationStatus.emailTemplateTypeIds).subscribe({
      next: allEmailTemplateIds => this.emailTemplatesIds = allEmailTemplateIds,
    })
  }
}

export interface ApplicationStatusDialogData {
  applicationId: number;
  candidateEmail: string;

  /** lock status dialog on rejection only (only reject status is available for selection) */
  isRejection: boolean;
  isSendEmailSelected: boolean;
  isGeneralApplication: boolean;

  selectionStepId: number | null;
}
